import { z } from "zod";
import {
  attendanceMarkSchema,
  bookingSourceSchema,
  classLevelSchema,
  bookingStatusSchema,
  enrollmentStatusSchema,
  ownerOverrideSchema,
  participantKindSchema,
  sessionStatusSchema,
} from "../enums.ts";
import {
  isoDateSchema,
  isoDateTimeSchema,
  timestampsSchema,
  uuidSchema,
} from "./common.ts";

export const sessionSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    groupId: uuidSchema,
    templateId: uuidSchema.nullable(),
    startsAt: isoDateTimeSchema,
    endsAt: isoDateTimeSchema,
    capacity: z.number().int().nullable(),
    status: sessionStatusSchema,
    movedToSessionId: uuidSchema.nullable(),
    cancelReason: z.string().nullable(),
    /** The session's own name ("Core awake") and what it is ("Mixed apparatus"), from its weekly time. Null: the class name. */
    name: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
  })
  .merge(timestampsSchema);
export type Session = z.infer<typeof sessionSchema>;

/** A session as the availability list shows it: enough to render a calendar cell and book from it. */
export const availableSessionSchema = sessionSchema.extend({
  groupName: z.string(),
  groupColor: z.string().nullable(),
  groupKind: z.enum(["group", "private"]).default("group"),
  /** The class is open to all levels: only a package that names it pays for it (`packagePaysFor`). */
  groupOpenToAll: z.boolean().default(false),
  /** The level badge of the class (migration 0074): "Beginner" on the card. Null: none. */
  groupLevel: classLevelSchema.nullable().optional(),
  staffName: z.string().nullable().default(null),
  /** Who runs this one session. The owner schedule reads it to preselect the staff change. */
  staffUserId: uuidSchema.nullable().default(null),
  durationMin: z.number().int(),
  bookedCount: z.number().int(),
  spotsLeft: z.number().int().nullable(),
  /** False when the booking window is closed, with `bookingBlockedReason` saying why. */
  bookable: z.boolean(),
  bookingBlockedReason: z.string().nullable(),
  /**
   * When the booking window is the reason: the moment the session opens for booking (its start less
   * the window of its class, or of the business). The Book tab says "Opens on Mon 3 Nov at 7:00 AM".
   */
  bookingOpensAt: isoDateTimeSchema.nullable().optional(),
  /**
   * The place in line of the person the list was asked for (`participantId`) on this session's
   * waiting list, 1 for the first. Null: not on it.
   */
  waitlistPosition: z.number().int().nullable().optional(),
  /** Whether a full session takes a waiting list now: it has a limit and starts in more than 1 hour. */
  waitlistOpen: z.boolean().optional(),
  /**
   * A live waitlist offer of this session for the person the list was asked for (B2): the place is
   * free for them until `expiresAt`. Null or left out: no offer.
   */
  waitlistOffer: z.object({ entryId: uuidSchema, expiresAt: isoDateTimeSchema }).nullable().optional(),
  /**
   * Owner list only: the people on the waiting list of the session (`session_waitlist`, status
   * waiting or offered). The calendar fill bar is gold with it (`fillBar`, D38).
   * Optional for older API builds and absent on member lists.
   */
  waitlistCount: z.number().int().min(0).optional(),
  /**
   * Owner list only: who came (#ai-feedback 2026-10-26). The bookings marked present, and the names
   * that wait for a parent and came (no booking yet). An ended session shows it as the main number.
   * Optional for older API builds and absent on member lists.
   */
  cameCount: z.number().int().min(0).optional(),
  /**
   * Owner list only: the confirmed bookings made at the session for someone who did not book
   * (`walk_in`). `bookedCount` holds them; an ended session shows Booked without them.
   */
  walkInCount: z.number().int().min(0).optional(),
});
export type AvailableSession = z.infer<typeof availableSessionSchema>;

export const availabilityQuerySchema = z.object({
  from: isoDateTimeSchema,
  to: isoDateTimeSchema,
  groupId: uuidSchema.optional(),
  /**
   * The person the member books for. A child sees only the sessions of their own group and of
   * classes open to all (and no other group session while they have none); the account holder sees
   * every session.
   */
  participantId: uuidSchema.optional(),
});
export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;

export const availabilityResponseSchema = z.object({ items: z.array(availableSessionSchema) });
export type AvailabilityResponse = z.infer<typeof availabilityResponseSchema>;

/** `POST /v1/owner/businesses/:businessId/sessions`, the ad-hoc case. */
export const createSessionRequestSchema = z.object({
  groupId: uuidSchema,
  startsAt: isoDateTimeSchema,
  /** Optional. Falls back to the group's duration. */
  endsAt: isoDateTimeSchema.optional(),
  capacity: z.number().int().min(1).max(500).nullable().optional(),
});
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;

/** `PATCH /v1/owner/businesses/:businessId/sessions/:id`, the move case. */
export const moveSessionRequestSchema = z.object({
  startsAt: isoDateTimeSchema.optional(),
  endsAt: isoDateTimeSchema.optional(),
  capacity: z.number().int().min(1).max(500).nullable().optional(),
  reason: z.string().max(500).optional(),
});
export type MoveSessionRequest = z.infer<typeof moveSessionRequestSchema>;

export const cancelSessionRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type CancelSessionRequest = z.infer<typeof cancelSessionRequestSchema>;

/** What a business cancel or move did, so the owner sees the blast radius immediately. */
export const sessionMutationResultSchema = z.object({
  session: sessionSchema,
  affectedBookings: z.number().int(),
  affectedUsers: z.number().int(),
  creditsReturned: z.number().int(),
  notificationsQueued: z.number().int(),
});
export type SessionMutationResult = z.infer<typeof sessionMutationResultSchema>;

/**
 * One person on a session, as the owner needs to read it before they walk on deck.
 *
 * The participant is the one attending, which for a swimming business is almost always a child. The
 * member is whoever booked and pays, which is the person the owner actually calls when somebody
 * does not turn up, so both names travel together and the phone number comes with them.
 *
 * Cancelled bookings are included rather than filtered out. "Two dropped out this morning" is the
 * fact an owner most wants from this list, and a list that silently omits them cannot tell them.
 */
export const sessionAttendeeSchema = z.object({
  bookingId: uuidSchema,
  status: bookingStatusSchema,
  source: bookingSourceSchema,
  participantId: uuidSchema,
  participantName: z.string(),
  participantKind: participantKindSchema,
  /** Null on an adult booking their own place, where the participant name is already the member. */
  memberName: z.string().nullable(),
  /** The account behind the booking, which is what "Message" on the register opens a thread with. */
  memberUserId: uuidSchema,
  /** Null for a member who signs in by email only. */
  memberPhone: z.string().nullable(),
  bookedAt: isoDateTimeSchema,
  cancelledAt: isoDateTimeSchema.nullable(),
  /** The policy engine's own words for why a cancellation cost what it did. Null while confirmed. */
  cancelNote: z.string().nullable(),
  /** Who came, as staff marked it. Null until marked. Optional for older API builds. */
  attendance: attendanceMarkSchema.nullable().optional(),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
  /** A yes on a health question here (C2): the flag "Health note", never the answers. Optional for older API builds. */
  healthNote: z.boolean().optional(),
  /** The person's first class at the business (C3, `services/first-class.ts`). Optional for older API builds. */
  firstClass: z.boolean().optional(),
  /**
   * Add anyway (#ai-feedback 2026-10-26): `owed` while the client owes this session (no package paid
   * it yet), `free` when the owner gave it. Null for an ordinary booking and for an owed session a
   * package paid. Optional for older API builds.
   */
  ownerOverride: ownerOverrideSchema.nullable().optional(),
});
export type SessionAttendee = z.infer<typeof sessionAttendeeSchema>;

/** A name that waits for a parent and came to the session: a visit with no booking yet (migration 0038). */
export const sessionWaitingVisitSchema = z.object({ id: uuidSchema, name: z.string() });
export type SessionWaitingVisit = z.infer<typeof sessionWaitingVisitSchema>;

export const sessionAttendeesResponseSchema = z.object({
  items: z.array(sessionAttendeeSchema),
  /**
   * The names that wait for a parent and came (#ai-feedback 2026-10-26). They have no booking, so
   * they are not in `items`. Optional for older API builds.
   */
  waiting: z.array(sessionWaitingVisitSchema).optional(),
});
export type SessionAttendeesResponse = z.infer<typeof sessionAttendeesResponseSchema>;

export const enrollmentSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    participantId: uuidSchema,
    templateId: uuidSchema,
    status: enrollmentStatusSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema.nullable(),
    participantName: z.string().optional(),
  })
  .merge(timestampsSchema);
export type Enrollment = z.infer<typeof enrollmentSchema>;

export const createEnrollmentRequestSchema = z.object({
  participantId: uuidSchema,
  templateId: uuidSchema,
  startsOn: isoDateSchema,
  endsOn: isoDateSchema.nullable().default(null),
});
export type CreateEnrollmentRequest = z.infer<typeof createEnrollmentRequestSchema>;

export const updateEnrollmentRequestSchema = z
  .object({
    status: enrollmentStatusSchema,
    endsOn: isoDateSchema.nullable(),
  })
  .partial();
export type UpdateEnrollmentRequest = z.infer<typeof updateEnrollmentRequestSchema>;
